// messageQueue — the rules for many messages (pen "Messages" §1), pure.
//
// A reducer over time-stamped inputs: every input carries `at` (ms, read by
// the shell), and nothing here reads a clock or sets a timer. The shell asks
// `nextDeadline` when to come back and feeds a `tick` then.
//
//   - Same message again (same dedupe key: kind + target): merged into the
//     one toast, counted ("×12"), its timer restarted, moved to the bottom.
//   - At most 3 toasts show, newest at the bottom; older ones fold into
//     "+N more" (`expand` shows them all).
//   - A burst (more than 5 new toasts within 2 s): one summary toast counts
//     them ("27 things happened while you worked"); the folded ones stay
//     reachable through `expand`.
//   - A sticky toast (an error that needs you) stays until closed.
//   - `pause` (hover, focus) holds every timer; `resume` shifts them by the
//     pause, so nothing expires while you read.
//   - One polite announcement per toast: `announcement` changes when a toast
//     is new (or a burst starts), never on a merge.
//
// Two single slots ride along: the last-step line (a step that worked where
// you can see it) and the note at an object (a problem with one object).
// Each newer one replaces the one before.
//
// Generic in the payload `P` (the shell's HostNotice); the routing of a
// payload to a surface is `messages.ts`.

export type {
    Announcement,
    Burst,
    MessageInput,
    MessageQueue,
    QueueControl,
    QueuedToast,
    QueueEvent,
    Slot,
};
export { emptyQueue, nextDeadline, receive, stackView };

import { exhaustSwitchCase } from "@bilda/utils";

const MAX_VISIBLE = 3;
// "More than 5 within 2 s".
const BURST_LIMIT = 5;
const BURST_WINDOW_MS = 2000;
// How long a burst summary stays after the last message in it.
const BURST_DWELL_MS = 6000;

// What to show and for how long. `dwell` is ignored when `sticky`.
type MessageInput<P> = {
    readonly surface: "toast" | "last-step" | "at-object";
    readonly key: string;
    readonly payload: P;
    readonly sticky: boolean;
    readonly dwell: number;
};

type QueuedToast<P> = {
    readonly id: number;
    readonly key: string;
    readonly payload: P;
    // How many times it arrived (1 = once).
    readonly count: number;
    readonly sticky: boolean;
    readonly dwell: number;
    // When its timer last (re)started.
    readonly shownAt: number;
};

type Burst = {
    readonly id: number;
    readonly count: number;
    readonly lastAt: number;
};

type Slot<P> = {
    readonly id: number;
    readonly payload: P;
    readonly dwell: number;
    readonly shownAt: number;
};

type Announcement<P> =
    | { readonly type: "toast"; readonly id: number; readonly payload: P }
    | { readonly type: "burst"; readonly id: number; readonly count: number };

type MessageQueue<P> = {
    // Oldest first.
    readonly toasts: readonly QueuedToast<P>[];
    readonly burst: Burst | null;
    // When the recent new toasts arrived (the burst window).
    readonly arrivals: readonly number[];
    readonly lastStep: Slot<P> | null;
    readonly atObject: Slot<P> | null;
    readonly pausedAt: number | null;
    readonly expanded: boolean;
    readonly announcement: Announcement<P> | null;
    readonly nextId: number;
};

// The stack's own inputs (the clock, hover/focus, the close and "+N more"
// buttons); a new message is `show`.
type QueueControl =
    | { readonly type: "tick"; readonly at: number }
    | { readonly type: "pause"; readonly at: number }
    | { readonly type: "resume"; readonly at: number }
    | { readonly type: "dismiss"; readonly id: number }
    | { readonly type: "expand" }
    | { readonly type: "collapse" };

type QueueEvent<P> =
    | {
          readonly type: "show";
          readonly at: number;
          readonly input: MessageInput<P>;
      }
    | QueueControl;

const emptyQueue: MessageQueue<never> = {
    toasts: [],
    burst: null,
    arrivals: [],
    lastStep: null,
    atObject: null,
    pausedAt: null,
    expanded: false,
    announcement: null,
    nextId: 1,
};

const showToast = <P>(
    q: MessageQueue<P>,
    at: number,
    input: MessageInput<P>,
): MessageQueue<P> => {
    const same = q.toasts.find((t) => t.key === input.key);
    if (same !== undefined)
        return {
            ...q,
            toasts: [
                ...q.toasts.filter((t) => t.id !== same.id),
                {
                    ...same,
                    payload: input.payload,
                    count: same.count + 1,
                    sticky: same.sticky || input.sticky,
                    dwell: input.dwell,
                    shownAt: at,
                },
            ],
        };
    const arrivals = [
        ...q.arrivals.filter((a) => at - a < BURST_WINDOW_MS),
        at,
    ];
    const toast: QueuedToast<P> = {
        id: q.nextId,
        key: input.key,
        payload: input.payload,
        count: 1,
        sticky: input.sticky,
        dwell: input.dwell,
        shownAt: at,
    };
    const bursting =
        !input.sticky && (q.burst !== null || arrivals.length > BURST_LIMIT);
    const burst: Burst | null = !bursting
        ? q.burst
        : q.burst === null
          ? { id: q.nextId + 1, count: arrivals.length, lastAt: at }
          : { ...q.burst, count: q.burst.count + 1, lastAt: at };
    const announcement: Announcement<P> | null = !bursting
        ? { type: "toast", id: toast.id, payload: input.payload }
        : q.burst === null && burst !== null
          ? { type: "burst", id: burst.id, count: burst.count }
          : q.announcement;
    return {
        ...q,
        toasts: [...q.toasts, toast],
        arrivals,
        burst,
        announcement,
        nextId: q.nextId + 2,
    };
};

const slotOf = <P>(
    q: MessageQueue<P>,
    at: number,
    input: MessageInput<P>,
): Slot<P> => ({
    id: q.nextId,
    payload: input.payload,
    dwell: input.dwell,
    shownAt: at,
});

const show = <P>(
    q: MessageQueue<P>,
    at: number,
    input: MessageInput<P>,
): MessageQueue<P> => {
    switch (input.surface) {
        case "toast":
            return showToast(q, at, input);
        case "last-step":
            return {
                ...q,
                lastStep: slotOf(q, at, input),
                nextId: q.nextId + 1,
            };
        case "at-object":
            return {
                ...q,
                atObject: slotOf(q, at, input),
                nextId: q.nextId + 1,
            };
        default:
            return exhaustSwitchCase(input.surface);
    }
};

const alive = (at: number, shownAt: number, dwell: number): boolean =>
    at < shownAt + dwell;

const liveSlot = <P>(slot: Slot<P> | null, at: number): Slot<P> | null =>
    slot !== null && alive(at, slot.shownAt, slot.dwell) ? slot : null;

// Drop what has run out. Returns `q` itself when nothing did.
const tick = <P>(q: MessageQueue<P>, at: number): MessageQueue<P> => {
    if (q.pausedAt !== null) return q;
    const toasts = q.toasts.filter(
        (t) => t.sticky || alive(at, t.shownAt, t.dwell),
    );
    const burst =
        q.burst !== null && alive(at, q.burst.lastAt, BURST_DWELL_MS)
            ? q.burst
            : null;
    const lastStep = liveSlot(q.lastStep, at);
    const atObject = liveSlot(q.atObject, at);
    const unchanged =
        toasts.length === q.toasts.length &&
        burst === q.burst &&
        lastStep === q.lastStep &&
        atObject === q.atObject;
    return unchanged
        ? q
        : {
              ...q,
              toasts,
              burst,
              lastStep,
              atObject,
              expanded: q.expanded && toasts.length > MAX_VISIBLE,
          };
};

const shiftSlot = <P>(slot: Slot<P> | null, by: number): Slot<P> | null =>
    slot === null ? null : { ...slot, shownAt: slot.shownAt + by };

const resume = <P>(q: MessageQueue<P>, at: number): MessageQueue<P> => {
    if (q.pausedAt === null) return q;
    const by = at - q.pausedAt;
    return {
        ...q,
        pausedAt: null,
        toasts: q.toasts.map((t) => ({ ...t, shownAt: t.shownAt + by })),
        burst:
            q.burst === null
                ? null
                : { ...q.burst, lastAt: q.burst.lastAt + by },
        lastStep: shiftSlot(q.lastStep, by),
        atObject: shiftSlot(q.atObject, by),
    };
};

// Closing the burst summary closes what it folded (all but the sticky ones).
const dismiss = <P>(q: MessageQueue<P>, id: number): MessageQueue<P> =>
    q.burst?.id === id
        ? { ...q, burst: null, toasts: q.toasts.filter((t) => t.sticky) }
        : {
              ...q,
              toasts: q.toasts.filter((t) => t.id !== id),
              lastStep: q.lastStep?.id === id ? null : q.lastStep,
              atObject: q.atObject?.id === id ? null : q.atObject,
          };

const receive = <P>(q: MessageQueue<P>, e: QueueEvent<P>): MessageQueue<P> => {
    switch (e.type) {
        case "show":
            return show(tick(q, e.at), e.at, e.input);
        case "tick":
            return tick(q, e.at);
        case "pause":
            return q.pausedAt === null ? { ...q, pausedAt: e.at } : q;
        case "resume":
            return resume(q, e.at);
        case "dismiss":
            return dismiss(q, e.id);
        case "expand":
            return q.expanded ? q : { ...q, expanded: true };
        case "collapse":
            return q.expanded ? { ...q, expanded: false } : q;
        default:
            return exhaustSwitchCase(e);
    }
};

// When something next runs out, or null (nothing will, or paused).
const nextDeadline = <P>(q: MessageQueue<P>): number | null => {
    if (q.pausedAt !== null) return null;
    const deadlines = [
        ...q.toasts.filter((t) => !t.sticky).map((t) => t.shownAt + t.dwell),
        ...(q.burst === null ? [] : [q.burst.lastAt + BURST_DWELL_MS]),
        ...[q.lastStep, q.atObject].flatMap((s) =>
            s === null ? [] : [s.shownAt + s.dwell],
        ),
    ];
    return deadlines.length === 0 ? null : Math.min(...deadlines);
};

// What the stack shows: the toasts (oldest first), how many are folded into
// "+N more", and the burst summary (shown below them) if one is running.
type StackView<P> = {
    readonly toasts: readonly QueuedToast<P>[];
    readonly more: number;
    readonly burst: Burst | null;
};

const stackView = <P>(q: MessageQueue<P>): StackView<P> => {
    if (q.expanded) return { toasts: q.toasts, more: 0, burst: q.burst };
    // In a burst only the sticky toasts show beside the summary.
    const candidates =
        q.burst === null ? q.toasts : q.toasts.filter((t) => t.sticky);
    const room = q.burst === null ? MAX_VISIBLE : MAX_VISIBLE - 1;
    const toasts = candidates.slice(-room);
    return { toasts, more: candidates.length - toasts.length, burst: q.burst };
};
